import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type IAutoCompleteGroup,
  type IAutoCompleteOptions
} from '@/components/Shared/ui/autocomplete';
import { DictTypes } from '@/constants';
import { setCustomOption } from '@/services';
import { isDictHasAll, safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import type { CarModalType } from '../car-info-modal';

export type KeyToUse = 'uuid' | 'code';

function generateYears(): IAutoCompleteOptions[] {
  const currentYear = new Date().getFullYear();
  const years: IAutoCompleteOptions[] = [];

  for (let i = 0; i <= 50; i++) {
    const year = (currentYear - i).toString();
    years.push({ value: year, label: year });
  }

  return years;
}

export interface CarInfoInterface {
  mark: string;
  model: string;
  year: string;
  equipment?: string;
  dealer?: string;
}

interface IProps {
  keyToUse: KeyToUse;
  defaultValues?: CarInfoInterface;
  type: CarModalType;
  forceSetDealer: (dealer: string) => void;
}

const useCarInfo = ({
  keyToUse,
  defaultValues,
  type,
  forceSetDealer
}: IProps) => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { dealer: userDealer } = useAppSelector((state) => state.userInfo);

  const globalDealer =
    dicts?.[DictTypes.Dealers]?.getBy.code[
      safeKey(orderApp?.productInfo.product.dealer ?? userDealer?.code)
    ];

  const isAutoCreditLeasing = type === 'autoCreditLeasing';

  const [possibleMarks, setPossibleMarks] = useState<OptionsInterface[]>([]);
  const [possibleModels, setPossibleModels] = useState<
    Array<
      OptionsInterface & {
        isCommercial: boolean;
      }
    >
  >([]);
  const [possibleEquipments, setPossibleEquipments] = useState<
    OptionsInterface[]
  >([]);
  const [possibleDealers, setPossibleDealers] = useState<OptionsInterface[]>(
    []
  );

  // Набор возможных дилеров для формы
  const possibleDealerUuidsSet: Set<string> = useMemo(() => {
    if (!globalDealer || !isDictHasAll(dicts, [DictTypes.Dealers]))
      return new Set();

    const dealerUuids: string[] =
      globalDealer._dealerUuids.length === 0
        ? [globalDealer.uuid]
        : globalDealer._dealerUuids;

    return new Set(
      dealerUuids.filter(
        (uuid) => dicts[DictTypes.Dealers].getBy.uuid[uuid]?.is_active
      )
    );
  }, [dicts, globalDealer]);

  const models = useMemo(() => {
    return possibleModels.reduce((acc, item) => {
      if (!item.isCommercial) {
        acc[0] ??= { groupTitle: '', options: [] };
        acc[0].options.push(item);
        return acc;
      }

      acc[1] ??= { groupTitle: 'Коммерческие', options: [] };
      acc[1].options.push(item);
      return acc;
    }, [] as IAutoCompleteGroup[]);
  }, [possibleModels]);

  const years = useMemo(() => generateYears(), []);

  const handleDealerChanged = useCallback(
    (markLabel: string, dealerFindValue: string) => {
      if (
        !isAutoCreditLeasing ||
        !isDictHasAll(dicts, [
          DictTypes.Models,
          DictTypes.Dealers,
          DictTypes.Brands
        ])
      )
        return;

      const dealer = dicts[DictTypes.Dealers].getBy[keyToUse][dealerFindValue];
      const mark = dicts[DictTypes.Brands].getBy.name[markLabel];

      const possibleModelUuidsSetForLeasing = new Set(
        (dicts?.[DictTypes.LeasingIndividualsSettings]?.content ?? [])
          .filter(
            (item) =>
              item.is_active &&
              item.brand_uuid === mark?.uuid &&
              item.dealer_uuid === dealer?.uuid
          )
          .map((item) => item.model_uuid)
      );

      const optionsHandler = dicts[DictTypes.Models].content
        .filter(
          (model) =>
            possibleModelUuidsSetForLeasing.has(model.uuid) && model.is_active
        )
        .map((data) => {
          return {
            value: keyToUse === 'uuid' ? data.uuid : data.code.toString(),
            label: data.name,
            isCommercial: !!data.is_commercial
          };
        })
        .sort((a, b) => {
          if (a.isCommercial !== b.isCommercial) {
            return a.isCommercial ? 1 : -1;
          }
          return a.label.localeCompare(b.label);
        });

      setPossibleModels(optionsHandler);
    },
    [dicts, isAutoCreditLeasing]
  );

  const handleMarkChanged = useCallback(
    (findValue: string) => {
      if (
        !isDictHasAll(dicts, [
          DictTypes.Brands,
          DictTypes.Models,
          DictTypes.Dealers,
          DictTypes.DealersUsedDealers,
          DictTypes.Equipments
        ])
      )
        return;

      const mark = dicts[DictTypes.Brands].getBy[keyToUse][findValue];

      if (!isAutoCreditLeasing) {
        const possibleModelsHandler = dicts[DictTypes.Models].content.filter(
          (model) => model.brand_uuid === mark?.uuid && model.is_active
        );

        const optionsHandler = possibleModelsHandler
          .map((data) => {
            return {
              value: keyToUse === 'uuid' ? data.uuid : data.code.toString(),
              label: data.name,
              isCommercial: !!data.is_commercial
            };
          })
          .sort((a, b) => {
            if (a.isCommercial !== b.isCommercial) {
              return a.isCommercial ? 1 : -1;
            }
            return a.label.localeCompare(b.label);
          });

        setPossibleModels(optionsHandler);
      }

      const possibleDealerUuidsSetByMark = new Set(
        dicts[DictTypes.Dealers].content
          .filter((dealer) => {
            return (
              dealer.is_active &&
              dealer._brandUuids.includes(mark?.uuid ?? '') &&
              possibleDealerUuidsSet.has(dealer.uuid)
            );
          })
          .map((item) => item.uuid)
      );

      const possibleDealerUuidsSetForLeasingByMark = new Set(
        (isAutoCreditLeasing
          ? (dicts?.[DictTypes.LeasingIndividualsSettings]?.content ?? [])
          : []
        )
          .filter(
            (item) =>
              item.is_active &&
              item.brand_uuid === mark?.uuid &&
              possibleDealerUuidsSet.has(item.dealer_uuid)
          )
          .map((item) => item.dealer_uuid)
      );

      const possibleDealerUuidsSetToCheck = isAutoCreditLeasing
        ? possibleDealerUuidsSetForLeasingByMark
        : possibleDealerUuidsSetByMark;

      const possibleDealersHandler = dicts[DictTypes.Dealers].content.filter(
        (dealer) => {
          return (
            dealer.is_active &&
            dealer._brandUuids.includes(mark?.uuid ?? '') &&
            possibleDealerUuidsSetToCheck.has(dealer.uuid)
          );
        }
      );

      const dealersOptionsHandler = setCustomOption(
        possibleDealersHandler,
        keyToUse === 'uuid'
      );

      setPossibleDealers(dealersOptionsHandler);

      // Обязательная установка если дилер единственный в списке
      if (
        possibleDealerUuidsSetToCheck.size === 1 &&
        possibleDealersHandler.length === 1 &&
        possibleDealerUuidsSetToCheck.has(possibleDealersHandler[0].uuid)
      ) {
        const dealerValue = dealersOptionsHandler[0];
        handleDealerChanged(mark?.name || '', dealerValue.value);
        forceSetDealer(dealerValue.label);
      } else {
        forceSetDealer('');
      }
    },
    [dicts, isAutoCreditLeasing]
  );

  const handleModelChanged = useCallback(
    (markLabel: string, dealerLabel: string, modelFindValue: string) => {
      if (
        !isDictHasAll(dicts, [
          DictTypes.Equipments,
          DictTypes.Models,
          DictTypes.Dealers,
          DictTypes.Brands
        ])
      )
        return;

      const model = dicts[DictTypes.Models].getBy[keyToUse][modelFindValue];

      if (isAutoCreditLeasing) {
        const dealer = dicts[DictTypes.Dealers].getBy.name[dealerLabel];
        const mark = dicts[DictTypes.Brands].getBy.name[markLabel];

        const possibleEquipmentUuidsSetForLeasing = new Set(
          (dicts?.[DictTypes.LeasingIndividualsSettings]?.content ?? [])
            .filter(
              (item) =>
                item.is_active &&
                item.model_uuid === model?.uuid &&
                item.dealer_uuid === dealer?.uuid &&
                item.brand_uuid === mark?.uuid
            )
            .map((item) => item.equipment_uuid)
        );

        const optionsHandler = dicts[DictTypes.Equipments].content
          .filter(
            (equipment) =>
              possibleEquipmentUuidsSetForLeasing.has(equipment.uuid) &&
              equipment.is_active
          )
          .map((data) => {
            return {
              value: data.uuid,
              label: data.name
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setPossibleEquipments(optionsHandler);

        return;
      }

      const equipments =
        dicts[DictTypes.Equipments].content.filter(
          (equipment) =>
            equipment.model_uuid === model?.uuid && equipment.is_active
        ) || [];

      const optionsHandler = equipments
        .map((data) => {
          return {
            value: data.uuid,
            label: data.name
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      if (optionsHandler.length === 0) {
        optionsHandler.push({
          value: '',
          label: 'Стандарт'
        });
      }
      setPossibleEquipments(optionsHandler);
    },
    [dicts, isAutoCreditLeasing]
  );

  useEffect(() => {
    if (!isDictHasAll(dicts, [DictTypes.Brands])) return;

    if (isAutoCreditLeasing) {
      const possibleBrandsUuidsSet = new Set(
        (dicts?.[DictTypes.LeasingIndividualsSettings]?.content ?? [])
          .filter(
            (item) =>
              item.is_active && possibleDealerUuidsSet.has(item.dealer_uuid)
          )
          .map((item) => item.brand_uuid)
      );

      const possibleMarksHandler = dicts[DictTypes.Brands].content.filter(
        (brand) => possibleBrandsUuidsSet.has(brand.uuid)
      );

      const optionsHandler = setCustomOption(
        possibleMarksHandler,
        keyToUse === 'uuid'
      );

      setPossibleMarks(optionsHandler);
      setPossibleDealers([]);
      setPossibleModels([]);
      setPossibleEquipments([]);
      return;
    }

    if (globalDealer) {
      const possibleMarksHandler = dicts[DictTypes.Brands].content.filter(
        (brand) => {
          if (globalDealer?.name.includes('Hongqi')) {
            return (
              globalDealer?._brandUuids?.includes(brand.uuid) ||
              brand.name.toLowerCase() === 'faw'
            );
          }

          return globalDealer?._brandUuids?.includes(brand.uuid);
        }
      );

      const optionsHandler = setCustomOption(
        possibleMarksHandler,
        keyToUse === 'uuid'
      );
      setPossibleMarks(optionsHandler);
    }
  }, [globalDealer, dicts, isAutoCreditLeasing, possibleDealerUuidsSet]);

  useEffect(() => {
    const mark = possibleMarks.find(
      (item) => item.label === defaultValues?.mark
    );
    if (mark) handleMarkChanged(mark.value);
  }, [defaultValues?.mark, possibleMarks]);

  return {
    models,
    possibleModels,
    possibleMarks,
    possibleEquipments,
    possibleDealers,
    years,
    handleMarkChanged,
    handleModelChanged,
    handleDealerChanged
  };
};

export default useCarInfo;

interface OptionsInterface {
  value: string;
  label: string;
}
